/* Copyright (c) 2020-present CloudIO Inc */

import { DataRecord } from '@cloudio-saas/datasource-types';
import { evalX } from '@cloudio-saas/quickx';
import {
  emptyFunction,
  Path,
  selector,
  StateXGetter,
  StateXSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import { ClassNameMap, Theme } from '@mui/material';
import { DataGridItem } from 'componentregistry/components/DataGrid/DataGrid';
import { ControllerInterface } from 'controller/ControllerInterface';
import { useContext, useMemo } from 'react';
import { AnyColumn, Platform } from '../../../componentregistry';
import PlatformVariableProvider from '../../../expression/VariableProvider';
import { UserSetup } from '../../../root/useUser';
import Store from '../../../store/Store';
import { getErrorMessage, isEmpty } from '../../../utils/isEmpty';
import { HeaderRowsContext } from '../../DataGrid/Context';
import { ColumnData } from '../../DataGrid/grid/types';
import { SwitchColumnCaseItem } from 'componentregistry/components/DataGrid/columns/SwitchColumn/SwitchColumnCase';
import { itemWithDefaultValues } from 'registry/Components';

function _setValue<T>({
  viewAttribute,
  store,
  recordId,
  set,
  get,
  ssa,
}: {
  viewAttribute?: string;
  recordId: string;
  store: Store<DataRecord>;
  set: StateXSetter;
  get: StateXGetter;
  ssa: React.SetStateAction<T>;
}) {
  if (isEmpty(viewAttribute)) return;
  let val: T;
  if (typeof ssa === 'function') {
    // @ts-ignore ssa is a function
    val = ssa(get([...store.recordsPath, recordId, attribute]));
  } else {
    val = ssa;
  }
  // @ts-ignore Partial<S>
  const updatedRecord = store.processUpdateRecord(recordId, {
    [viewAttribute]: val,
  });

  const attributePath: Path = [...store.recordsPath, recordId, viewAttribute];

  // update the attribute to avoid cursor jump issue
  set(attributePath, val);
  // and then update the record for the rest
  set([...store.recordsPath, recordId], updatedRecord);
}

const EMPTY_ARRAY: ColumnData[] = [];

const rowSelector = selector<
  ColumnData[],
  {
    platform: Platform;
    data: Record<string, unknown>;
    columns: AnyColumn[];
    region: string;
    store: Store<DataRecord> | null;
    classes: ClassNameMap<
      | 'cell'
      | 'center'
      | 'end'
      | 'start'
      | 'overflowVisible'
      | 'error'
      | 'input'
    >;
    userSetup: UserSetup;
    controller?: ControllerInterface;
    theme: Theme;
  }
>({
  path: [
    'transient',
    'rowSelector',
    ':appUid',
    ':pageId',
    ':gridId',
    ':rowIndex',
  ],
  get: (
    { get, set, params, getOnce },
    {
      data,
      platform,
      columns,
      region,
      store,
      classes,
      userSetup,
      controller,
      theme,
    },
  ): ColumnData[] => {
    if (!store) return EMPTY_ARRAY;
    const { appUid, pageId, gridId, rowIndex } = params as {
      appUid: string;
      pageId: string;
      gridId: string;
      rowIndex: number;
    };
    const gridItem = platform.getItem(appUid, pageId, gridId) as DataGridItem;
    const {
      insertable: gridInsertable = { value: false, type: 'boolean' },
      updatable: gridUpdatable = { value: false, type: 'boolean' },
    } = gridItem?.props ?? {}; // List used for select

    const pp = new PlatformVariableProvider(
      platform,
      {
        appUid,
        pageId,
        itemId: gridId,
        rowIndex,
        dsAlias: store.alias,
        property: 'row',
        data,
      },
      get,
    );

    const updatable =
      gridUpdatable.type === 'boolean'
        ? gridUpdatable.value
        : !!evalX(gridUpdatable.value, pp);

    const insertable =
      gridInsertable.type === 'boolean'
        ? gridInsertable.value
        : !!evalX(gridInsertable.value, pp);

    const recordId = get([...store.recordsOrderPath, rowIndex]) as string;
    const totalRecords = store.size();
    return columns.map((column, columnIndex): ColumnData => {
      let switchColumn;
      if (column.type === 'SwitchColumn') {
        const { expression } = column.props;
        const expResult = expression
          ? platform.evalIfExpressionString(
              appUid,
              pageId,
              column.itemId,
              'expression',
              { rowIndex, dsAlias: store.alias },
              expression,
            )
          : '';
        const switchCases = platform.getChildrenItemsById(
          appUid,
          pageId,
          column.itemId,
          (child) => {
            const caseValue = (child as SwitchColumnCaseItem).props.case;
            const matchedChild =
              child.type === 'SwitchColumnCase' &&
              (!caseValue || caseValue === expResult);

            if (matchedChild && (child.props.visible ?? true)) {
              if (!child.props.visibleCondition) {
                return true;
              } else {
                return platform.evalBoolean(
                  appUid,
                  pageId,
                  child.itemId,
                  'visibleCondition',
                  { rowIndex, dsAlias: store.alias },
                  child.props.visibleCondition,
                );
              }
            }
            return false;
          },
        ) as SwitchColumnCaseItem[];
        if (switchCases.length > 0 && switchCases[0].children.length > 0) {
          switchColumn = platform.getItem(
            appUid,
            pageId,
            switchCases[0].children[0],
          ) as AnyColumn;
          if (switchColumn) {
            switchColumn = itemWithDefaultValues(switchColumn);
          }
        }
      }
      const itemId = switchColumn?.itemId ?? column.itemId;
      const { colorExpression, bgColorExpression, viewAttribute, alignment } =
        switchColumn?.props ?? column.props;

      let color: string | undefined;
      let bgColor: string | undefined;
      let exp: string | undefined;
      let value: string | number | null | undefined;
      let setValue: (ssa: React.SetStateAction<unknown>) => void =
        emptyFunction;
      if (rowIndex >= 0 && rowIndex < totalRecords) {
        try {
          color =
            typeof colorExpression === 'string'
              ? colorExpression
              : colorExpression?.value;
          if (colorExpression?.type === 'expression') {
            exp = colorExpression.value;
            color = evalX(exp, pp);
          }
          bgColor =
            typeof bgColorExpression === 'string'
              ? bgColorExpression
              : bgColorExpression?.value;
          if (bgColorExpression?.type === 'expression') {
            exp = bgColorExpression.value;
            bgColor = evalX(exp, pp);
          }
        } catch (e) {
          if (!data.donotShowError) {
            platform.showError(
              e instanceof Error
                ? `${e.name} evaluating expression [${exp}]: ${e.message}`
                : getErrorMessage(e),
              {
                appUid,
                pageId,
                itemId,
              },
            );
          }
        }

        value = viewAttribute
          ? (get([...store.recordsPath, recordId, viewAttribute]) as
              | string
              | number
              | null
              | undefined)
          : undefined;

        setValue = (ssa: React.SetStateAction<unknown>) => {
          _setValue({ viewAttribute, recordId, store, get: getOnce, set, ssa });
        };
      }

      return {
        activeRow: false,
        alignment,
        bgColor,
        color,
        column,
        columnIndex,
        itemId,
        region,
        value,
        viewAttribute,
        recordId,
        store,
        setValue,
        classes,
        userSetup,
        gridUpdatable: updatable,
        gridInsertable: insertable,
        controller,
        theme,
      };
    });
  },
});

export default function useRow({
  columns,
  rowIndex,
  appUid,
  pageId,
  store,
  platform,
  region,
  gridId,
  classes,
  userSetup,
  controller,
  theme,
}: {
  gridId: string;
  appUid: string;
  pageId: string;
  columns: AnyColumn[];
  rowIndex: number;
  store: Store<DataRecord> | null;
  platform: Platform;
  region: string;
  classes: ClassNameMap<
    'cell' | 'center' | 'end' | 'start' | 'overflowVisible' | 'error' | 'input'
  >;
  userSetup: UserSetup;
  controller?: ControllerInterface;
  theme: Theme;
}): ColumnData[] {
  const headerRows = useContext(HeaderRowsContext);
  rowIndex = rowIndex - headerRows;

  const props = useMemo(() => {
    // console.log('useRow', rowIndex);
    return {
      platform,
      data: { rowIndex },
      columns,
      region,
      store,
      classes,
      userSetup,
      controller,
      theme,
    };
  }, [
    classes,
    columns,
    controller,
    platform,
    region,
    rowIndex,
    store,
    userSetup,
    theme,
  ]);

  return useStateXSelector(rowSelector, {
    params: { appUid, pageId, gridId, rowIndex },
    props,
  });
}
